import {
  Brain,
  Sparkles,
  Zap,
  CheckCircle2,
  CloudDownload,
  Smartphone,
  WifiOff,
  Bell,
  Database,
  ShieldCheck,
  Zap as SpeedIcon,
  Globe,
  Terminal,
  AtSign,
  Menu,
  X
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useState, useEffect } from 'react';
import './index.css';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-slate-900/40 backdrop-blur-3xl shadow-[0_8px_32px_0_rgba(15,20,27,0.35)]' : 'bg-transparent'}`}>
      <div className="flex justify-between items-center w-full px-8 py-4 max-w-screen-2xl mx-auto">
        <div className="text-xl font-black tracking-tighter text-slate-100">guandolo.com</div>

        <div className="hidden md:flex gap-8 items-center font-medium text-sm">
          <a className="text-slate-300 hover:text-white transition-colors" href="#">Platform</a>
          <a className="text-blue-300 border-b-2 border-blue-400/50 pb-1" href="#">Solutions</a>
          <a className="text-slate-300 hover:text-white transition-colors" href="#">Resources</a>
          <a className="text-slate-300 hover:text-white transition-colors" href="#">Pricing</a>
        </div>

        <div className="flex gap-4 items-center">
          <button className="hidden lg:block text-slate-300 text-sm font-medium hover:text-white transition-colors px-4 py-2">Sign In</button>
          <button className="bg-gradient-to-r from-primary to-primary-container text-on-primary font-bold px-6 py-2.5 rounded-full hover:scale-105 active:scale-95 transition-all shadow-lg shadow-primary/20">
            Get Started
          </button>
          <button className="md:hidden text-slate-100" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            {isMobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden bg-slate-900/95 backdrop-blur-3xl border-b border-white/10 px-8 py-6 flex flex-col gap-4"
          >
            <a className="text-slate-300 hover:text-white transition-colors py-2" href="#">Platform</a>
            <a className="text-blue-300 py-2" href="#">Solutions</a>
            <a className="text-slate-300 hover:text-white transition-colors py-2" href="#">Resources</a>
            <a className="text-slate-300 hover:text-white transition-colors py-2" href="#">Pricing</a>
            <button className="text-slate-300 text-sm font-medium hover:text-white transition-colors py-2 text-left">Sign In</button>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

const Hero = () => (
  <section className="max-w-7xl mx-auto mb-32 flex flex-col items-center text-center pt-32 px-6">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-secondary-container/20 border border-outline-variant/15 backdrop-blur-md mb-8"
    >
      <span className="w-2 h-2 rounded-full bg-tertiary animate-pulse"></span>
      <span className="text-[10px] uppercase tracking-widest font-bold text-secondary">The Future of PWA</span>
    </motion.div>

    <motion.h1
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tight mb-8 leading-[1.1] text-on-surface"
    >
      We Build AI-Powered Apps <br />
      <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary via-secondary to-primary-container">
        That Feel Like Magic
      </span>
    </motion.h1>

    <motion.p
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="max-w-2xl text-lg md:text-xl text-on-surface-variant/80 font-light leading-relaxed mb-12"
    >
      Hyper-responsive progressive web applications that leverage world-class artificial intelligence to automate, inspire, and elevate your user experience.
    </motion.p>

    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="flex flex-col sm:flex-row gap-6"
    >
      <button onClick={() => window.location.href = "./contact"} className="px-10 py-4 bg-gradient-to-r from-primary to-primary-container text-on-primary font-extrabold rounded-full text-lg shadow-xl shadow-primary/10 hover:shadow-primary/30 transition-all">
        Start Your Project
      </button>
      <button onClick={() => window.location.href = "./teacher"} className="px-10 py-4 bg-surface-bright/20 backdrop-blur-xl border border-outline-variant/20 rounded-full font-bold text-on-surface hover:bg-surface-bright/30 transition-all">
        View Showcase
      </button>
    </motion.div>
  </section>
);

const AIPowered = () => (
  <section className="max-w-7xl mx-auto mb-40 px-6">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-5">
        <h2 className="text-3xl md:text-4xl font-bold mb-6 text-on-surface">Powered by the <br />Best AI</h2>
        <p className="text-on-surface-variant text-lg mb-8 leading-relaxed">
          We integrate the frontier models directly into your workflow. No matter the task, we utilize the specific engine best suited for the mission.
        </p>
        <ul className="space-y-4">
          {[
            "DeepSeek for complex logic synthesis",
            "Gemini for multi-modal reasoning",
            "OpenAI for natural conversation flows"
          ].map((text, i) => (
            <li key={i} className="flex items-center gap-3">
              <CheckCircle2 className="text-primary w-5 h-5" />
              <span className="font-medium">{text}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { name: 'Gemini', desc: 'High-performance multimodal models', icon: <Brain className="text-blue-300 w-8 h-8" />, color: 'from-blue-500/20 to-blue-400/40', mt: 'mt-0' },
          { name: 'OpenAI', desc: 'Industry-leading reasoning capabilities', icon: <Sparkles className="text-emerald-300 w-8 h-8" />, color: 'from-emerald-500/20 to-emerald-400/40', mt: 'md:mt-8' },
          { name: 'DeepSeek', desc: 'Optimized coding & logic excellence', icon: <Zap className="text-purple-300 w-8 h-8" />, color: 'from-purple-500/20 to-purple-400/40', mt: 'md:mt-16' }
        ].map((ai, i) => (
          <motion.div
            key={i}
            whileHover={{ y: -5 }}
            className={`p-8 rounded-3xl bg-surface-container-lowest/10 backdrop-blur-2xl border border-outline-variant/10 flex flex-col items-center text-center ${ai.mt}`}
          >
            <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${ai.color} flex items-center justify-center mb-6`}>
              {ai.icon}
            </div>
            <h3 className="font-bold text-xl mb-2">{ai.name}</h3>
            <p className="text-xs text-on-surface-variant opacity-60">{ai.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const BentoGrid = () => (
  <section className="max-w-7xl mx-auto mb-40 px-6">
    <div className="text-center mb-16">
      <h2 className="text-3xl md:text-5xl font-black mb-4">The Best of Web + Mobile</h2>
      <p className="text-on-surface-variant">PWAs bridge the gap between reach and performance.</p>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 auto-rows-[250px]">
      <div className="md:col-span-8 md:row-span-1 p-8 rounded-[2rem] bg-surface-container-lowest/10 backdrop-blur-2xl border border-outline-variant/10 flex flex-col justify-end relative overflow-hidden">
        <div className="absolute top-8 right-8 text-primary opacity-20 transform scale-150">
          <CloudDownload size={120} />
        </div>
        <h3 className="text-3xl font-bold mb-2">Zero Friction</h3>
        <p className="text-on-surface-variant max-w-md">No app stores, no downloads, no updates. Just one click and your users are in.</p>
      </div>

      <div className="md:col-span-4 md:row-span-2 p-8 rounded-[2rem] bg-surface-container-low/20 backdrop-blur-3xl border border-primary/20 flex flex-col items-center justify-center text-center group">
        <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center mb-8 group-hover:scale-110 transition-transform">
          <Smartphone className="text-primary w-12 h-12" />
        </div>
        <h3 className="text-2xl font-bold mb-4">Home Screen Presence</h3>
        <p className="text-sm text-on-surface-variant leading-relaxed">Installs natively on Android and iOS with a custom brand icon and splash screen.</p>
      </div>

      <div className="md:col-span-4 md:row-span-1 p-8 rounded-[2rem] bg-surface-container-lowest/10 backdrop-blur-2xl border border-outline-variant/10 flex flex-col justify-end">
        <WifiOff className="text-tertiary w-10 h-10 mb-4" />
        <h3 className="text-xl font-bold mb-2">Offline Access</h3>
        <p className="text-xs text-on-surface-variant">Critical data remains available even without a connection.</p>
      </div>

      <div className="md:col-span-4 md:row-span-1 p-8 rounded-[2rem] bg-surface-container-lowest/10 backdrop-blur-2xl border border-outline-variant/10 flex flex-col justify-end">
        <Bell className="text-secondary w-10 h-10 mb-4" />
        <h3 className="text-xl font-bold mb-2">Push Ready</h3>
        <p className="text-xs text-on-surface-variant">Re-engage your users with native push notifications.</p>
      </div>
    </div>
  </section>
);

const BackendSection = () => (
  <section className="max-w-7xl mx-auto mb-40 px-6">
    <div className="p-12 rounded-[3rem] bg-surface-container-lowest/5 backdrop-blur-md border border-outline-variant/10 relative overflow-hidden">
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary/10 blur-[120px] rounded-full"></div>
      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div>
          <div className="inline-block px-4 py-1 bg-tertiary-container/20 text-tertiary-fixed rounded-lg text-[10px] font-black uppercase tracking-widest mb-6">Backend Architecture</div>
          <h2 className="text-4xl md:text-5xl font-black mb-8 leading-tight">Scalable. Secure. <br />Reliable.</h2>

          <div className="space-y-8">
            {[
              { title: 'Real-time Database', desc: 'Instant synchronization across all clients with Firebase Firestore.', icon: <Database className="text-primary" /> },
              { title: 'Secure Auth', desc: 'Enterprise-grade security with multi-factor authentication built-in.', icon: <ShieldCheck className="text-primary" /> },
              { title: 'Fast Hosting', desc: 'Global CDN delivery ensuring your app loads in milliseconds.', icon: <SpeedIcon className="text-primary" /> }
            ].map((item, i) => (
              <div key={i} className="flex gap-6">
                <div className="shrink-0 w-12 h-12 rounded-xl bg-surface-variant/40 flex items-center justify-center">
                  {item.icon}
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-1">{item.title}</h4>
                  <p className="text-on-surface-variant text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative group">
          <div className="absolute -inset-1 bg-gradient-to-r from-primary/30 to-secondary/30 rounded-3xl blur-2xl group-hover:blur-3xl transition-all"></div>
          <div className="relative rounded-3xl bg-surface-container-high overflow-hidden shadow-2xl">
            <img
              className="w-full h-auto object-cover aspect-video grayscale hover:grayscale-0 transition-all duration-700"
              alt="modern tech interface dashboard"
              src="https://lh3.googleusercontent.com/aida-public/AB6AXuA78aC4cAvbugvaHUHFWzgzHu0Bz6uMiVQC_mYO1fwNjN0X6i5Xq5xUnf8IQlrx8FbDcyvmpTZWRHDmFtE95U53ugcnbnChfURrjIY22zdkYy3kVfsZ_P5aBXr5ImITp9apW4ZXMVGBI3TONPfEnYDDo9_M-d8sKp_12Hqul2LJojMHO52yJ5iTyNZw9uRbIu1GsXL8T5KZ1WoWPPcQNqTQKvZyYB3St89eIi6JkrNOeAGxv4eu9nIDC3Hj4N44WZVpGfuzPMiigyU"
              referrerPolicy="no-referrer"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
);

const HowItWorks = () => (
  <section className="max-w-7xl mx-auto mb-40 px-6">
    <div className="text-center mb-20">
      <h2 className="text-3xl md:text-5xl font-black mb-4">How It Works</h2>
      <div className="h-1 w-24 bg-gradient-to-r from-primary to-transparent mx-auto"></div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
      {[
        { step: '1', title: 'Tell Us What You Want', desc: "Share your vision. Whether it's an AI personal trainer or a corporate fleet manager, we translate your ideas into technical specs." },
        { step: '2', title: 'Choose Your Domain', desc: 'Pick the perfect name. We handle the hosting, SSL certification, and deployment architecture across global regions.' },
        { step: '3', title: 'Launch Your App', desc: 'Within days, your AI-powered PWA is live. Scalable from your first 10 users to your first 10 million.' }
      ].map((item, i) => (
        <div key={i} className="relative">
          <div className="text-9xl font-black text-outline-variant/10 absolute -top-12 -left-4 select-none">{item.step}</div>
          <div className="relative z-10 pt-10">
            <h3 className="text-2xl font-bold mb-4">{item.title}</h3>
            <p className="text-on-surface-variant leading-relaxed">{item.desc}</p>
          </div>
        </div>
      ))}
    </div>
  </section>
);

const CTA = () => (
  <section className="max-w-5xl mx-auto px-6 mb-40">
    <div className="p-16 rounded-[4rem] bg-gradient-to-br from-primary-container/40 to-surface-container-lowest/40 backdrop-blur-3xl border border-white/10 text-center shadow-2xl">
      <h2 className="text-4xl md:text-6xl font-black mb-8">Turn Your Idea <br />Into an AI App</h2>
      <p className="text-lg md:text-xl text-on-surface-variant mb-12 max-w-xl mx-auto">
        Stop waiting for developers and start building with the magicians of the modern web.
      </p>
      <div className="flex flex-col items-center gap-4">
        <button onClick={() => window.location.href = "./contact"} className="px-12 py-5 bg-white text-surface font-black rounded-full text-xl hover:bg-primary transition-colors hover:text-on-primary">
          Contact guandolo
        </button>
        <p className="text-xs text-on-surface-variant uppercase tracking-widest font-bold opacity-60">Consultation is free</p>
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer className="w-full mt-auto border-t border-white/10 bg-slate-950/60 backdrop-blur-2xl px-6">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 px-12 py-16 max-w-7xl mx-auto">
      <div>
        <div className="text-lg font-bold text-slate-200 mb-6">guandolo.com</div>
        <p className="text-slate-400 text-xs tracking-wide leading-loose max-w-sm mb-8">
          Elevating the digital experience through atmospheric design and artificial intelligence. We build the interfaces of tomorrow.
        </p>
        <div className="flex gap-6">
          <a className="text-slate-400 hover:text-primary transition-colors" href="#"><Globe size={20} /></a>
          <a className="text-slate-400 hover:text-primary transition-colors" href="#"><Terminal size={20} /></a>
          <a className="text-slate-400 hover:text-primary transition-colors" href="#"><AtSign size={20} /></a>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-8">
        <div className="space-y-4">
          <h4 className="text-xs tracking-wide uppercase font-semibold text-blue-300">Quick Links</h4>
          <ul className="space-y-2">
            <li><a className="text-slate-400 hover:text-blue-200 text-xs transition-colors" href="#">Documentation</a></li>
            <li><a className="text-slate-400 hover:text-blue-200 text-xs transition-colors" href="#">Security</a></li>
            <li><a className="text-slate-400 hover:text-blue-200 text-xs transition-colors" href="#">Status</a></li>
          </ul>
        </div>
        <div className="space-y-4">
          <h4 className="text-xs tracking-wide uppercase font-semibold text-blue-300">Legal</h4>
          <ul className="space-y-2">
            <li><a className="text-slate-400 hover:text-blue-200 text-xs transition-colors" href="#">Privacy Policy</a></li>
            <li><a className="text-slate-400 hover:text-blue-200 text-xs transition-colors" href="#">Terms of Service</a></li>
            <li><a className="text-slate-400 hover:text-blue-200 text-xs transition-colors" href="#">Contact Us</a></li>
          </ul>
        </div>
      </div>
      <div className="md:col-span-2 pt-8 mt-8 border-t border-white/5 text-center">
        <p className="text-[10px] tracking-wide uppercase font-semibold text-slate-500">
          © 2024 guandolo.com. Elevated by The Celestial Canvas.
        </p>
      </div>
    </div>
  </footer>
);

export default function Pwa() {
  return (
    <div className="min-h-screen flex flex-col relative">
      {/* Fixed Atmospheric Background
      <div className="fixed inset-0 z-[-1] celestial-bg opacity-40 overflow-hidden" />
      <div className="fixed inset-0 z-[-1] bg-gradient-to-b from-surface-container-lowest/20 via-transparent to-surface-container-lowest/80" />
 */}
      {/* <Navbar /> */}

      <main className="flex-grow">
        <Hero />
        <AIPowered />
        <BentoGrid />
        <BackendSection />
        <HowItWorks />
        <CTA />
      </main>

      {/* <Footer /> */}
    </div>
  );
}
